/* eslint-disable @typescript-eslint/no-explicit-any */
import { Extension } from "@tiptap/core";
import type { SuggestionProps } from "@tiptap/suggestion";
import Suggestion from "@tiptap/suggestion";
import {
  Code,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  List,
  ListOrdered,
  Quote,
  Table as TableIcon,
  Video as VideoIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

interface CommandItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  command: (props: { editor: any; range: any }) => void;
  searchTerms: string[];
}

export const commands: CommandItem[] = [
  {
    title: "Image",
    description: "Upload or select an image",
    icon: <ImageIcon className="w-5 h-5" />,
    searchTerms: ["image", "picture", "photo", "img"],
    command: ({ editor, range }) => {
      // Dispatch custom event to open image picker
      window.dispatchEvent(
        new CustomEvent("openImagePicker", {
          detail: { editor, range },
        })
      );
    },
  },
  {
    title: "Video",
    description: "Upload or embed a video",
    icon: <VideoIcon className="w-5 h-5" />,
    searchTerms: ["video", "movie", "embed"],
    command: ({ editor, range }) => {
      // Dispatch custom event to open video source modal
      window.dispatchEvent(
        new CustomEvent("openVideoSourceModal", {
          detail: { editor, range },
        })
      );
    },
  },
  {
    title: "Table",
    description: "Insert a table with rows and columns",
    icon: <TableIcon className="w-5 h-5" />,
    searchTerms: ["table", "grid"],
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },
  {
    title: "Code Block",
    description: "Add code with syntax highlighting",
    icon: <Code className="w-5 h-5" />,
    searchTerms: ["code", "codeblock"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  {
    title: "Heading 1",
    description: "Large section heading",
    icon: <Heading1 className="w-5 h-5" />,
    searchTerms: ["heading", "h1", "title"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleHeading({ level: 1 }).run();
    },
  },
  {
    title: "Heading 2",
    description: "Medium section heading",
    icon: <Heading2 className="w-5 h-5" />,
    searchTerms: ["heading", "h2", "subtitle"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleHeading({ level: 2 }).run();
    },
  },
  {
    title: "Heading 3",
    description: "Small section heading",
    icon: <Heading3 className="w-5 h-5" />,
    searchTerms: ["heading", "h3"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleHeading({ level: 3 }).run();
    },
  },
  {
    title: "Bullet List",
    description: "Create a bullet list",
    icon: <List className="w-5 h-5" />,
    searchTerms: ["list", "bullet", "unordered"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: "Numbered List",
    description: "Create a numbered list",
    icon: <ListOrdered className="w-5 h-5" />,
    searchTerms: ["list", "numbered", "ordered"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: "Quote",
    description: "Create a block quote",
    icon: <Quote className="w-5 h-5" />,
    searchTerms: ["quote", "blockquote"],
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
];

interface CommandMenuPropsType extends Omit<SuggestionProps, "command"> {
  items: CommandItem[];
  command: (item: CommandItem) => void;
}

export function CommandMenu({ items, command }: CommandMenuPropsType) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    },
    [items, command]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % items.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        selectItem(selectedIndex);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [items, selectedIndex, selectItem]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl w-64 p-4 text-center text-sm text-gray-500">
        No commands found
      </div>
    );
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-2xl w-64 max-h-80 overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-200 ease-out"
    >
      <div className="py-2">
        {items.map((cmd, index) => (
          <button
            key={cmd.title}
            onClick={() => selectItem(index)}
            className={`w-full px-3 py-2.5 text-left flex items-center gap-3 transition-all duration-150 ${
              selectedIndex === index ? "bg-blue-100" : "hover:bg-gray-50"
            }`}
          >
            <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 flex-shrink-0">
              {cmd.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 text-sm">{cmd.title}</div>
              <div className="text-xs text-gray-500">{cmd.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Helper to position the popup menu
function updatePopupPosition(props: any, popup: HTMLDivElement | null) {
  if (!popup) return;

  const { clientRect } = props;
  if (clientRect) {
    const rect = typeof clientRect === "function" ? clientRect() : clientRect;
    if (rect) {
      popup.style.position = "fixed";
      popup.style.top = `${rect.bottom + 8}px`;
      popup.style.left = `${rect.left}px`;
      popup.style.zIndex = "99999";
    }
  } else {
    // Fallback: position at cursor if clientRect is not available
    popup.style.position = "fixed";
    popup.style.top = "100px";
    popup.style.left = "100px";
    popup.style.zIndex = "99999";
  }
}

export const SlashCommands = Extension.create({
  name: "slashCommands",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        startOfLine: false,
        allowSpaces: false,
        allowedPrefixes: null, // Allow trigger after any character (not just space)
        command: ({ editor, range, props }: any) => {
          props.command({ editor, range });
        },
      },
    };
  },

  addProseMirrorPlugins() {
    const { suggestion } = this.options;

    return [
      Suggestion({
        ...suggestion,
        editor: this.editor,
        items: ({ query }: { query: string }) => {
          return commands.filter((cmd) => {
            const searchText = query.toLowerCase();
            return (
              cmd.title.toLowerCase().includes(searchText) ||
              cmd.description.toLowerCase().includes(searchText) ||
              cmd.searchTerms.some((term) => term.includes(searchText))
            );
          });
        },
        render: () => {
          let component: ReturnType<typeof createRoot> | null = null;
          let popup: HTMLDivElement | null = null;

          return {
            onStart: (props: any) => {
              popup = document.createElement("div");
              popup.className = "slash-command-menu-popup";
              document.body.appendChild(popup);
              component = createRoot(popup);

              updatePopupPosition(props, popup);

              component.render(
                <CommandMenu
                  {...props}
                  command={(item: CommandItem) => {
                    item.command({
                      editor: props.editor,
                      range: props.range,
                    });
                  }}
                />
              );
            },
            onUpdate: (props: any) => {
              updatePopupPosition(props, popup);

              component?.render(
                <CommandMenu
                  {...props}
                  command={(item: CommandItem) => {
                    item.command({
                      editor: props.editor,
                      range: props.range,
                    });
                  }}
                />
              );
            },
            onKeyDown: (props: any) => {
              if (props.event.key === "Escape") {
                popup?.remove();
                component?.unmount();
                return true;
              }
              return false;
            },
            onExit: () => {
              popup?.remove();
              component?.unmount();
              popup = null;
              component = null;
            },
          };
        },
      }),
    ];
  },
});
